/**
 * Keep a PageContext inside the server's limits (strings <= 300, screen.text <= 4000,
 * selectedIds <= 50, screen lists per their caps, other lists <= 20, JSON <= 31 KB) —
 * the server REJECTS oversize payloads (417) instead of truncating, so we shrink here,
 * least useful parts first. `screen` is the bulkiest part, so it shrinks first.
 */

import { PAGE_CONTEXT_LIMITS, type PageContext, type ScreenContext, type ScreenSection } from "./types";

const encoder = new TextEncoder();

export function contextBytes(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).length;
}

/** Per-path list caps (array items keep their parent's path). */
const LIST_CAPS: Record<string, number> = {
  "list.selectedIds": PAGE_CONTEXT_LIMITS.maxSelectedIds,
  recent: PAGE_CONTEXT_LIMITS.maxRecent,
  "screen.sections": PAGE_CONTEXT_LIMITS.maxScreenSections,
  "screen.sections.kpis": PAGE_CONTEXT_LIMITS.maxScreenKpis,
  "screen.sections.charts": PAGE_CONTEXT_LIMITS.maxScreenCharts,
  "screen.sections.fields": PAGE_CONTEXT_LIMITS.maxScreenFields,
  "screen.sections.table.rows": PAGE_CONTEXT_LIMITS.maxScreenRows,
  "screen.sections.table.columns": PAGE_CONTEXT_LIMITS.maxScreenColumns,
};

/** Per-path string caps; everything else is maxString. */
const STRING_CAPS: Record<string, number> = {
  "screen.text": PAGE_CONTEXT_LIMITS.maxScreenText,
};

function clampValue(value: unknown, path: string, depth: number): unknown {
  if (typeof value === "string") {
    const cap = STRING_CAPS[path] ?? PAGE_CONTEXT_LIMITS.maxString;
    return value.length > cap ? value.slice(0, cap) : value;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value === null || typeof value !== "object") return value;
  if (depth > 6) return undefined;
  if (Array.isArray(value)) {
    const cap = LIST_CAPS[path] ?? PAGE_CONTEXT_LIMITS.maxListItems;
    return value.slice(0, cap).map((item) => clampValue(item, path, depth + 1));
  }
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (item === undefined) continue;
    const next = clampValue(item, path ? `${path}.${key}` : key, depth + 1);
    if (next !== undefined) out[key] = next;
  }
  return out;
}

const withScreen = (ctx: PageContext, screen: ScreenContext): PageContext => ({
  ...ctx,
  screen: { ...screen, truncated: true },
});

const mapSections = (ctx: PageContext, fn: (s: ScreenSection) => ScreenSection): PageContext =>
  ctx.screen ? withScreen(ctx, { ...ctx.screen, sections: ctx.screen.sections.map(fn) }) : ctx;

const halveRows = (s: ScreenSection): ScreenSection =>
  s.table ? { ...s, table: { ...s.table, rows: s.table.rows.slice(0, Math.floor(s.table.rows.length / 2)) } } : s;

/**
 * Screen shrink steps, in order: halve text -> halve table rows -> fields beyond 30 ->
 * drop charts -> drop screen. `applies` says whether a step can still shrink; repeatable
 * steps run until the payload fits or they can't shrink any more.
 */
const SCREEN_STEPS: Array<{ applies: (screen: ScreenContext) => boolean; run: (ctx: PageContext) => PageContext; repeat?: boolean }> = [
  {
    applies: (screen) => !!screen.text,
    run: (ctx) => withScreen(ctx, { ...ctx.screen!, text: ctx.screen!.text!.slice(0, Math.floor(ctx.screen!.text!.length / 2)) || undefined }),
    repeat: true,
  },
  {
    applies: (screen) => screen.sections.some((s) => !!s.table?.rows.length),
    run: (ctx) => mapSections(ctx, halveRows),
    repeat: true,
  },
  {
    applies: (screen) => screen.sections.some((s) => (s.fields?.length ?? 0) > PAGE_CONTEXT_LIMITS.screenFieldsWhenShrunk),
    run: (ctx) => mapSections(ctx, (s) => (s.fields ? { ...s, fields: s.fields.slice(0, PAGE_CONTEXT_LIMITS.screenFieldsWhenShrunk) } : s)),
  },
  {
    applies: (screen) => screen.sections.some((s) => !!s.charts),
    run: (ctx) => mapSections(ctx, ({ charts: _charts, ...s }) => s),
  },
  { applies: () => true, run: (ctx) => ({ ...ctx, screen: undefined }) },
];

function shrinkScreen(input: PageContext, maxBytes: number): PageContext {
  let ctx = input;
  for (const step of SCREEN_STEPS) {
    do {
      if (!ctx.screen || contextBytes(ctx) <= maxBytes || !step.applies(ctx.screen)) break;
      ctx = step.run(ctx);
    } while (step.repeat);
  }
  return ctx;
}

/** Shrink steps, least useful first. Each returns a smaller copy (or the same object if n/a). */
const SHRINK_STEPS: Array<(ctx: PageContext) => PageContext> = [
  (ctx) => (ctx.recent.length ? { ...ctx, recent: ctx.recent.map(({ path, pageType, title, at }) => ({ path, pageType, title, at })) } : ctx),
  (ctx) => (ctx.record?.fields ? { ...ctx, record: { ...ctx.record, fields: undefined } } : ctx),
  (ctx) => (ctx.recent.length ? { ...ctx, recent: [] } : ctx),
  (ctx) => (ctx.list?.filters.length ? { ...ctx, list: { ...ctx.list, filters: [] } } : ctx),
  (ctx) => (ctx.user.appAccess?.length ? { ...ctx, user: { ...ctx.user, appAccess: null } } : ctx),
];

export function fitPageContext(input: PageContext, maxBytes: number = PAGE_CONTEXT_LIMITS.maxBytes): PageContext {
  let ctx = clampValue(input, "", 0) as PageContext;
  if (ctx.screen) ctx = shrinkScreen(ctx, maxBytes);
  for (const step of SHRINK_STEPS) {
    if (contextBytes(ctx) <= maxBytes) return ctx;
    ctx = step(ctx);
  }
  // Selected ids: halve until it fits (selectedCount keeps the real number)
  while (contextBytes(ctx) > maxBytes && ctx.list && ctx.list.selectedIds.length > 0) {
    ctx = { ...ctx, list: { ...ctx.list, selectedIds: ctx.list.selectedIds.slice(0, Math.floor(ctx.list.selectedIds.length / 2)) } };
  }
  if (contextBytes(ctx) > maxBytes && ctx.extra) ctx = { ...ctx, extra: undefined };
  if (contextBytes(ctx) > maxBytes && ctx.list) ctx = { ...ctx, list: undefined };
  if (contextBytes(ctx) > maxBytes && ctx.record) ctx = { ...ctx, record: { doctype: ctx.record.doctype, name: ctx.record.name, title: ctx.record.title } };
  return ctx;
}
